/* `/zadania/rodzaje` — the three group cards, and the switch on every row.
 *
 * A screen of its own under a cog, not a drawer (owner, 18 September 2026). The crumb leads back to
 * `/zadania`, following `pages/reports/ui/QuestionShelfScreen`'s own back control. */

.page {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.crumb {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  align-self: flex-start;
  margin-bottom: var(--space-10px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);

  /* Nothing visible: it is what the focus outline follows, so the back link's ring is not a
   * rectangle standing off the words' corners. */
  border-radius: var(--radius-5px);
}

.crumb:hover {
  color: var(--text);
}

.crumbGlyph {
  width: 15px;
  height: 15px;
}

.title {
  font-size: var(--fs-screen-title);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-screen-title);
  line-height: var(--lh-tight);
  color: var(--text);
}

.sub {
  /* The ⓘ sits on the lede's own line, so the explanation has a place without a block of its own. */
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  min-height: 19px;
  margin-top: var(--space-6px);
  font-size: var(--fs-label);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* The sentence saying the setting lives on the account and the phone reads the same one. */


/* The three cards side by side, scrolling as one column on a short window. */
.scroller {
  flex: 1;
  min-height: 0;
  margin-top: var(--space-5);
  overflow-y: auto;
}

.cols {
  display: grid;

  /* `auto-fit` rather than a hard `repeat(3, …)`: the owner found the third column CLIPPED with no
   * scroll path at his window. Three tracks fit from about 900px of content width — every desktop
   * from 1280 up — and below that the third card WRAPS onto a second line, where the scroller can
   * reach it. A fixed three never yields, so the only thing it can do when it runs out of room is
   * leave a card where nothing can get at it. */
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));

  /* The canon's own 18px between the cards (`.setpage .cols`). `--space-5` IS 18px. */
  gap: var(--space-5);
  align-items: start;
  padding-bottom: var(--space-4);
}

.card {
  min-width: 0;

  /* `.setpage .card` in the canon: 8px 22px 12px. The rows breathe sideways, which is most of what
   * made the owner call them cramped; `--space-22px` is the exact value. */
  padding: var(--space-2) var(--space-22px) var(--space-3);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl);
}

.groupHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  /* `.setpage .set-sec`: 14px over the heading, 4px under it. */
  margin: var(--space-14px) var(--space-0) var(--space-1);
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.groupCount {
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.row {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-10px) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.rowText {
  flex: 1;
  min-width: 0;
}

.name {
  /* The canon's `.set-row .nm` is 13.5px. `--fs-inbox-name` is the repo's 13.5 — it names a person
   * in a list, which is what this is. `--fs-label-md` (13) had the names reading a step small
   * beside the 12px description under them. */
  font-size: var(--fs-inbox-name);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-name);
  color: var(--text);
}

.desc {
  margin-top: var(--space-1px);
  font-size: var(--fs-label);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* `ReasonTile` at this list's corner. Under the row, so it outranks the tile's own corner by
 * specificity rather than by stylesheet chunk order. */
.row .tile {
  border-radius: var(--radius-sm);
}

/* A muted kind. The tile goes neutral and the name steps back — the switch's own `aria-checked` is
 * what states it to assistive technology, so this is never the only channel. */
.rowMuted .name {
  color: var(--text-2);
}

.rowMuted .tile {
  color: var(--text-3);
  background: var(--bg-chip);
}

/* The refusal, on the ROW that was refused — this app has no toasts and one is not being added.
 * It is announced as well, through the screen's live region. */
.rowError {
  margin-top: var(--space-1px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--danger-text-strong);
}

/* ── The level (`KindLevel`) ───────────────────────────────────────────────────────────────────
 * On a line of its own under the description, the same height in every state, so the switch never
 * reflows the row. The groove, its two ends and the readout are `shared/ui/stepper`; what is here
 * is the line, the field inside the groove and what stands beside it. */

.levelLine {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-height: var(--h-prog-chip);
  margin-top: var(--space-6px);
}

.level {
  flex: none;
}

/* The readout is a text field: it takes the stepper's own type and none of the browser's. */
.levelValue {
  width: var(--w-prog-step-readout);
  height: var(--space-5);
  padding: var(--space-0);
  font-family: inherit;
  line-height: var(--lh-none);
  background: none;
  border: none;
  outline: none;
}

/* The kind is off: the level is kept and drawn, and says it is not in force. Still focusable. */
.rowMuted .level {
  background: var(--bg-chip);
  border-color: var(--border);
}

.rowMuted .levelValue {
  color: var(--text-2);
}

/* The stepper's ends ring in the page's ink here, as the switch, the crumb and the reset beside
 * them do. The shared stepper's accent ring is the progression sheet's, where every ring is one. */
.level button:focus-visible {
  outline-color: var(--text);
}

/* The field's focus is drawn on the groove, which is the control the trainer sees. */
.level:has(.levelValue:focus-visible) {
  border-color: var(--text);
  box-shadow: var(--ring-field);
}

/* `.level.levelInvalid`, so the error ring outranks the stepper's own border and the muted row's
 * by specificity rather than by which stylesheet chunk happened to load last. */
.level.levelInvalid,
.level.levelInvalid:has(.levelValue:focus-visible) {
  border-color: var(--danger);
  box-shadow: var(--ring-field-error);
}

.levelNote {
  min-width: 0;
  font-size: var(--fs-micro);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.levelReset {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;

  /* The line's own height, so the words are a 24px target without the line growing. */
  min-block-size: var(--h-prog-chip);
  padding: var(--space-0);
  font-family: inherit;
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
  cursor: pointer;
  background: none;
  border: none;
  border-radius: var(--radius-5px);
}

.levelResetGlyph {
  width: 12px;
  height: 12px;
}

.levelReset:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* ── The switch ────────────────────────────────────────────────────────────────────────────── */

/* A real `role="switch"` button, so the state reaches assistive technology as a state rather than
 * as a shape. It stays focusable in every state — an immediate write, and nothing to disable. */
.switch {
  position: relative;
  flex: none;
  width: 38px;
  height: 22px;
  background: var(--bg-sunken);
  border: 0;
  border-radius: var(--r-pill);
  box-shadow: var(--ring-field);
}

.switch::after {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  content: '';
  background: var(--text-2);
  border-radius: var(--r-circle);
  transition: transform var(--dur-fast) var(--ease-default);
}

.switch[aria-checked='true'] {
  background: var(--text);
  box-shadow: none;
}

.switch[aria-checked='true']::after {
  background: var(--bg-card);
  transform: translateX(16px);
}

.switch:focus-visible,
.crumb:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* ── The short states ──────────────────────────────────────────────────────────────────────── */

.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  margin-top: var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl);
}

.retry {
  margin-top: var(--space-4);
}

.barRow {
  display: block;
  height: 54px;
  margin-top: var(--space-10px);
  border-radius: var(--radius-13px);
}
